import styled, { keyframes } from 'styled-components'
import { Link } from 'react-router-dom'

const checkAnim = keyframes`
  to {
    stroke-dashoffset: 0;
  }
`

type StyledProps = {
  isOwned?: boolean;
}

const StyledCoursePreviewItem = styled(Link)<StyledProps>`
  user-select: none;
  transition: 0.2s;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.gray[200]};

  button {
    height: 34px;
    display: flex;
    align-items: center;
    border: none;
    transition: 0.2s;
    border-radius: 6px;
    padding: 0 10px;
    font-weight: 600;
    user-select: none;
    background-color: ${({ theme }) => theme.colors.gray[100]};
    color: ${({ theme }) => theme.colors.gray[600]};

    .pricing-details {
      margin-left: 4px;

      .pricing-discount {
        margin-left: 6px;
        color: ${({ theme }) => theme.colors.gray[500]};
      }
    }

    span {
      position: relative;
      top: 1px;
    }

    svg {
      height: 18px;
      margin-right: 8px;

      &.check-icon {
        path {
          stroke-width: 2.2px;
          stroke-dasharray: 1000;
          stroke-dashoffset: 1000;
          animation: ${checkAnim} 15s linear forwards;
        }
      }
    }

    &:hover {
      cursor: pointer;
      background-color: ${({ theme }) => theme.colors.gray[200]};
    }
  }

  .course-image {
    position: relative;
    padding-top: 56.25%;
    overflow: hidden;
    background-color: ${({ theme }) => theme.colors.gray[100]};

    .course-preview-prompt {
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 2;
      background-color: ${({ theme }) => theme.colors.background}a4;

      .preview-btn {
        background-color: ${({ theme }) => theme.colors.background};
        color: ${({ theme }) => theme.colors.gray[600]};
        box-shadow: 0 0 0 1px #00000021;

        &:hover {
          background-color: ${({ theme }) => theme.colors.gray[200]};
        }
      }
    }

    .empty-icon {
      color: ${({ theme }) => theme.colors.gray[300]};
      height: 40%;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    hr {
      height: 14px;
      margin: 0 8px;
      width: 1px;
      border: 0;
      background-color: ${({ theme }) => theme.colors.background}66;
    }

    .bonus-label {
      font-weight: 600;
      align-items: center;
      font-size: 14px;
      line-height: 24px;
      flex-shrink: 0;
      height: 28px;
      padding: 0 8px;
      user-select: none;
      pointer-events: none;
      border-radius: 6px;
      display: flex;
      letter-spacing: 0.02rem;
      justify-content: center;
      right: 12px;
      top: 12px;
      position: absolute;
      overflow: hidden;
      background-color: ${({ theme }) => theme.colors.red[300]};
      color: ${({ theme }) => theme.colors.background};
      box-shadow: inset 0 0 0 1px #00000021;
    }

    img {
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &::after {
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      content: '';
      border-bottom: 1px solid #0000000f;
      pointer-events: none;
    }
  }

  .course-details {
    padding: 20px;

    .course-title {
      letter-spacing: -0.01rem;
      line-height: 24px;
      font-weight: 600;
      font-size: 1.2rem;
      color: ${({ theme }) => theme.colors.gray[700]};
      max-width: 280px;
      display: block;
    }

    .course-stats {
      padding: 0;
      list-style-type: none;
      display: flex;
      align-items: center;

      li {
        display: flex;
        align-items: center;
        font-size: 15px;
        font-weight: 600;
        padding: 4px 8px 4px 7px;
        border-radius: 6px;
        color: ${({ theme }) => theme.colors.gray[500]};
        background-color: ${({ theme }) => theme.colors.gray[100]};

        span {
          position: relative;
          top: 1px;
        }

        svg {
          height: 20px;
          margin-right: 6px;
        }

        &:not(:last-child) {
          margin-right: 8px;
        }
      }
    }

    .course-description-preview {
      line-height: 22px;
      margin: 10px 0;
      color: ${({ theme }) => theme.colors.gray[600]};
      display: -webkit-box;
      -webkit-line-clamp: 4;
      overflow: hidden;
      -webkit-box-orient: vertical;
    }

    .course-actions {
      display: flex;
      align-items: center;
      margin-top: 10px;

      button {
        height: 38px;
        display: flex;
        align-items: center;
        border: none;
        transition: 0.2s;
        border-radius: 6px;
        padding: 0 10px;
        font-weight: 600;
        user-select: none;
        font-size: 1rem;
        background-color: ${({ theme }) => theme.colors.background};
        border: 1px solid ${({ theme }) => theme.colors.gray[300]};
        color: ${({ theme }) => theme.colors.gray[700]};
        font-family: ${({ theme }) => theme.fonts.nunito};

        svg {
          height: 18px;
          margin-right: 8px;
        }

        &:hover {
          background-color: ${({ theme }) => theme.colors.gray[100]};
        }

        &:not(:last-child) {
          margin-right: 8px;
        }
      }
    }

    .course-progress {
      margin-top: 24px;
      pointer-events: none;

      label {
        font-size: 14px;
        font-weight: 700;
        color: ${({ theme }) => theme.colors.gray[600]};
      }

      progress {
        -webkit-appearance: none;
        appearance: none;
        width: 100%;
        height: 8px;
        margin-top: 12px;
        border-radius: 50rem;
        overflow: hidden;

        &::-webkit-progress-bar {
          background: ${({ theme }) => theme.colors.green[100]};
        }

        &::-webkit-progress-value {
          border-radius: 50rem;
          background: ${({ theme }) => theme.colors.green[500]};
          transition: 0.2s;
        }
      }
    }
  }

  .add-to-cart-btn {
    margin-top: 18px;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.gray[50]};
  }
`

export default StyledCoursePreviewItem
